import { forwardRef } from "react";
import { MapPin, Phone, Mail, Globe, Users, Heart, BadgeCheck, Dog, Cat, PawPrint } from "lucide-react";
import type { Shelter } from "@/types/shelter";
import shareCardBg from "@/assets/share-card-bg.png";

export type ProfileCardFormat = "post" | "story";

// Dimensions - same as MultiShelterShareCard for consistency
export const PROFILE_POST_WIDTH = 1080;
export const PROFILE_POST_HEIGHT = 1350; // 4:5 ratio for FB/IG posts
export const PROFILE_STORY_WIDTH = 1080;
export const PROFILE_STORY_HEIGHT = 1920; // 9:16 ratio for IG stories

// Calculate dynamic height based on content length
export const calculateProfileCardHeight = (
  shelter: { description?: string | null; volunteer_info?: string | null },
  format: ProfileCardFormat = "post"
): number => {
  const baseHeight = format === "story" ? PROFILE_STORY_HEIGHT : PROFILE_POST_HEIGHT;
  const description = shelter.description || "";
  const volunteerInfo = shelter.volunteer_info || "";
  
  // Count characters and newlines (newlines add significant height with pre-line)
  const descriptionNewlines = (description.match(/\n/g) || []).length;
  const volunteerNewlines = (volunteerInfo.match(/\n/g) || []).length;
  const totalNewlines = descriptionNewlines + volunteerNewlines;
  
  const totalTextLength = description.length + volunteerInfo.length;
  
  // Add extra height for long content and newlines
  // Every 150 chars adds ~100px, each newline adds ~40px
  const extraFromChars = Math.floor(totalTextLength / 150) * 100;
  const extraFromNewlines = totalNewlines * 40;
  const extraHeight = Math.min(1000, extraFromChars + extraFromNewlines);
  
  return baseHeight + extraHeight;
};

// Local alias to match React's internal `CrossOrigin` type.
type CrossOrigin = "anonymous" | "use-credentials" | "" | undefined;

interface ShareProfileCardProps {
  shelter: Shelter;
  backgroundDataUrl?: string;
  format?: ProfileCardFormat;
}

export const ShareProfileCard = forwardRef<HTMLDivElement, ShareProfileCardProps>(
  ({ shelter, backgroundDataUrl, format = "post" }, ref) => {
    const isStory = format === "story";
    const width = isStory ? PROFILE_STORY_WIDTH : PROFILE_POST_WIDTH;
    const height = isStory ? PROFILE_STORY_HEIGHT : PROFILE_POST_HEIGHT;
    const bgImage = backgroundDataUrl || shareCardBg;

    // Scale fonts for story format
    const scale = isStory ? 1.15 : 1;

    // CrossOrigin for external images
    const backgroundCrossOrigin: CrossOrigin | undefined = (() => {
      if (!bgImage || typeof window === "undefined") return undefined;
      if (bgImage.startsWith("data:") || bgImage.startsWith("blob:") || bgImage.startsWith("/")) return undefined;
      try {
        const origin = new URL(bgImage, window.location.href).origin;
        return origin !== window.location.origin ? ("anonymous" as const) : undefined;
      } catch {
        return undefined;
      }
    })();

    // Format contact info
    const formatPhone = () => {
      if (!shelter.phone) return null;
      return shelter.phone.split(/[,;]/)[0].trim();
    };

    const formatEmail = () => {
      if (!shelter.email) return null;
      return shelter.email.length > 30 ? shelter.email.substring(0, 30) + "..." : shelter.email;
    };

    const formatWebsite = () => {
      if (!shelter.website) return null;
      const clean = shelter.website.replace(/^https?:\/\//, "").split("/")[0];
      return clean.length > 28 ? clean.substring(0, 28) + "..." : clean;
    };

    // Calculate dynamic height and content length for layout adjustments
    const descriptionLength = shelter.description?.length || 0;
    const volunteerInfoLength = shelter.volunteer_info?.length || 0;
    const totalTextLength = descriptionLength + volunteerInfoLength;
    const cardHeight = calculateProfileCardHeight(shelter, format);

    return (
      <div
        ref={ref}
        style={{
          width,
          minHeight: cardHeight,
          backgroundColor: "#FFFFFF",
          fontFamily: "'Nunito', 'Quicksand', sans-serif",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* iOS-safe background */}
        <img
          src={bgImage}
          alt=""
          draggable={false}
          data-export-bg="true"
          crossOrigin={backgroundCrossOrigin}
          loading="eager"
          decoding="sync"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
            zIndex: 0,
          }}
        />

        <div
          style={{
            position: "relative",
            zIndex: 1,
            width: "100%",
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header - brand */}
          <div
            style={{
              padding: isStory ? "32px 60px 0" : "24px 50px 0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: isStory ? 10 : 8,
              flexShrink: 0,
            }}
          >
            <div
              style={{
                background: "#4E8A6E",
                borderRadius: isStory ? 14 : 12,
                width: isStory ? 52 : 44,
                height: isStory ? 52 : 44,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <PawPrint
                style={{
                  width: isStory ? 30 : 24,
                  height: isStory ? 30 : 24,
                  color: "#FFFFFF",
                }}
              />
            </div>
            <span
              style={{
                color: "#4E8A6E",
                fontSize: isStory ? 26 : 22,
                fontWeight: 700,
                fontFamily: "'Nunito', 'Quicksand', sans-serif",
                letterSpacing: "-0.01em",
                opacity: 0.9,
              }}
            >
              GdzieSchronisko
            </span>
          </div>

          {/* Main content */}
          <div
            style={{
              flex: 1,
              padding: isStory ? "0 80px" : "0 80px",
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              justifyContent: "center",
              marginTop: totalTextLength > 300 ? 0 : (isStory ? -60 : -40),
            }}
          >
            {/* Main white card */}
            <div
              style={{
                background: "#FFFFFF",
                borderRadius: 28,
                boxShadow: "0 8px 32px rgba(45, 90, 61, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
            {/* Card header - shelter name and location */}
            <div
              style={{
                padding: isStory ? "48px 52px 32px" : "40px 44px 28px",
                background: "#F3F4F6",
                position: "relative",
              }}
            >
              {/* Verified badge */}
              {shelter.organization_verified && (
                <div
                  style={{
                    position: "absolute",
                    top: isStory ? 24 : 20,
                    right: isStory ? 24 : 20,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    background: "#2D5A3D",
                    padding: isStory ? "10px 16px" : "8px 14px",
                    borderRadius: 10,
                  }}
                >
                  <BadgeCheck
                    style={{
                      width: isStory ? 18 : 15,
                      height: isStory ? 18 : 15,
                      color: "#FFFFFF",
                    }}
                  />
                  <span
                    style={{
                      fontSize: isStory ? 15 : 13,
                      fontWeight: 800,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      color: "#FFFFFF",
                    }}
                  >
                    Zweryfikowane
                  </span>
                </div>
              )}

              {/* Shelter name */}
              <h1
                style={{
                  fontSize: 44 * scale,
                  fontWeight: 900,
                  color: "#2D5A3D",
                  fontFamily: "'Nunito', 'Quicksand', sans-serif",
                  margin: 0,
                  lineHeight: 1.2,
                  letterSpacing: "-0.02em",
                  paddingRight: shelter.organization_verified ? 220 : 0,
                }}
              >
                {shelter.name}
              </h1>

              {/* Full address */}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  marginTop: 12,
                  fontSize: 24 * scale,
                  color: "#4B5563",
                  fontWeight: 500,
                }}
              >
                <MapPin style={{ width: 20 * scale, height: 20 * scale, flexShrink: 0, marginTop: 2 }} />
                <span>
                  {shelter.address && `${shelter.address}, `}
                  {shelter.postal_code && `${shelter.postal_code} `}
                  {shelter.city}
                  {shelter.region && `, woj. ${shelter.region.toLowerCase()}`}
                </span>
              </div>
            </div>

              {/* Card body */}
              <div
                style={{
                  padding: isStory ? "36px 52px 44px" : "32px 44px 40px",
                  display: "flex",
                  flexDirection: "column",
                  gap: isStory ? 28 : 24,
                }}
              >
                {/* Description */}
                {shelter.description && (
                  <p
                    style={{
                      fontSize: 24 * scale,
                      fontWeight: 500,
                      color: "#374151",
                      lineHeight: 1.5,
                      margin: 0,
                      whiteSpace: "pre-line",
                    }}
                  >
                    {shelter.description}
                  </p>
                )}

                {/* Features grid */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 16,
                  }}
                >
                  {/* Volunteering */}
                  <div
                    style={{
                      background: shelter.volunteering_available === true 
                        ? "linear-gradient(135deg, #5A9E7C 0%, #4A8E6C 100%)" 
                        : "#F3F4F6",
                      borderRadius: 16,
                      padding: isStory ? "24px 20px" : "20px 16px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <Users
                      style={{
                        width: 32 * scale,
                        height: 32 * scale,
                        color: shelter.volunteering_available === true ? "white" : "#9CA3AF",
                      }}
                    />
                    <span
                      style={{
                        fontSize: 20 * scale,
                        fontWeight: 700,
                        color: shelter.volunteering_available === true ? "white" : "#9CA3AF",
                      }}
                    >
                      Wolontariat
                    </span>
                    <span
                      style={{
                        fontSize: 16 * scale,
                        fontWeight: 600,
                        color: shelter.volunteering_available === true ? "rgba(255,255,255,0.85)" : "#9CA3AF",
                      }}
                    >
                      {shelter.volunteering_available === true ? "✓ Możliwy" : shelter.volunteering_available === false ? "Niedostępny" : "—"}
                    </span>
                  </div>

                  {/* Dog walking */}
                  <div
                    style={{
                      background: shelter.dog_walking_available === true 
                        ? "linear-gradient(135deg, #5A9E7C 0%, #4A8E6C 100%)" 
                        : "#F3F4F6",
                      borderRadius: 16,
                      padding: isStory ? "24px 20px" : "20px 16px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <Heart
                      style={{
                        width: 32 * scale,
                        height: 32 * scale,
                        color: shelter.dog_walking_available === true ? "white" : "#9CA3AF",
                      }}
                    />
                    <span
                      style={{
                        fontSize: 20 * scale,
                        fontWeight: 700,
                        color: shelter.dog_walking_available === true ? "white" : "#9CA3AF",
                      }}
                    >
                      Spacery z psami
                    </span>
                    <span
                      style={{
                        fontSize: 16 * scale,
                        fontWeight: 600,
                        color: shelter.dog_walking_available === true ? "rgba(255,255,255,0.85)" : "#9CA3AF",
                      }}
                    >
                      {shelter.dog_walking_available === true ? "✓ Możliwe" : shelter.dog_walking_available === false ? "Niedostępne" : "—"}
                    </span>
                  </div>
                </div>

                {/* Volunteer info */}
                {shelter.volunteer_info && (
                  <div
                    style={{
                      background: "#F0FDF4",
                      borderRadius: 12,
                      padding: isStory ? "20px 24px" : "16px 20px",
                      borderLeft: "4px solid #2D5A3D",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 8,
                      }}
                    >
                      <Users style={{ width: 18 * scale, height: 18 * scale, color: "#2D5A3D" }} />
                      <span
                        style={{
                          fontSize: 16 * scale,
                          fontWeight: 700,
                          color: "#2D5A3D",
                          textTransform: "uppercase",
                          letterSpacing: "0.03em",
                        }}
                      >
                        Informacje o wolontariacie
                      </span>
                    </div>
                    <p
                      style={{
                        fontSize: 20 * scale,
                        fontWeight: 500,
                        color: "#374151",
                        lineHeight: 1.4,
                        margin: 0,
                        whiteSpace: "pre-line",
                      }}
                    >
                      {shelter.volunteer_info}
                    </p>
                  </div>
                )}

                {/* Animals available */}
                {(shelter.dogs_available === true || shelter.cats_available === true) && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 32,
                      padding: "16px 0",
                    }}
                  >
                    {shelter.dogs_available === true && (
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div
                          style={{
                            background: "#dbeafe",
                            borderRadius: 12,
                            padding: 12,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Dog style={{ width: 28 * scale, height: 28 * scale, color: "#2563eb" }} />
                        </div>
                        <span style={{ fontSize: 24 * scale, fontWeight: 700, color: "#2563eb" }}>
                          Psy do adopcji
                        </span>
                      </div>
                    )}
                    {shelter.cats_available === true && (
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div
                          style={{
                            background: "#f3e8ff",
                            borderRadius: 12,
                            padding: 12,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Cat style={{ width: 28 * scale, height: 28 * scale, color: "#9333ea" }} />
                        </div>
                        <span style={{ fontSize: 24 * scale, fontWeight: 700, color: "#9333ea" }}>
                          Koty do adopcji
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Contact info */}
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: "16px 32px",
                    paddingTop: 8,
                    borderTop: "1px solid #E5E7EB",
                  }}
                >
                  {formatPhone() && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Phone style={{ width: 20 * scale, height: 20 * scale, color: "#2D5A3D" }} />
                      <span style={{ fontSize: 22 * scale, fontWeight: 600, color: "#374151" }}>
                        {formatPhone()}
                      </span>
                    </div>
                  )}
                  {formatEmail() && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Mail style={{ width: 20 * scale, height: 20 * scale, color: "#2D5A3D" }} />
                      <span style={{ fontSize: 20 * scale, fontWeight: 600, color: "#374151" }}>
                        {formatEmail()}
                      </span>
                    </div>
                  )}
                  {formatWebsite() && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Globe style={{ width: 20 * scale, height: 20 * scale, color: "#2D5A3D" }} />
                      <span style={{ fontSize: 20 * scale, fontWeight: 600, color: "#374151" }}>
                        {formatWebsite()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer CTA - same style as MultiShelterShareCard */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: isStory ? 10 : 8,
                marginTop: isStory ? 24 : 20,
              }}
            >
              <div
                style={{
                  background: "#4E8A6E",
                  width: "100%",
                  borderRadius: 9999,
                  padding: isStory ? "10px 20px" : "8px 16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: isStory ? 8 : 6,
                  boxSizing: "border-box",
                }}
              >
                <span
                  style={{
                    fontSize: isStory ? 18 : 16,
                    color: "rgba(255,255,255,0.85)",
                    fontWeight: 500,
                    fontFamily: "'Nunito', 'Quicksand', sans-serif",
                    whiteSpace: "nowrap",
                  }}
                >
                  Aktualne potrzeby sprawdzisz na
                </span>
                <span
                  style={{
                    fontSize: isStory ? 24 : 22,
                    color: "white",
                    fontWeight: 700,
                    fontFamily: "'Nunito', 'Quicksand', sans-serif",
                    whiteSpace: "nowrap",
                  }}
                >
                  GdzieSchronisko.pl
                </span>
              </div>
            </div>
          </div>

          {/* Bottom padding */}
          <div style={{ height: isStory ? 40 : 32, flexShrink: 0 }} />
        </div>
      </div>
    );
  }
);

ShareProfileCard.displayName = "ShareProfileCard";
